import { useState, useEffect, useContext, useCallback, memo, useRef } from 'react';
// store
import { useSelector} from 'store';
// @devextreme
import Chart, { ChartTypes, CommonSeriesSettings, Series, Legend, Tooltip, Point, Crosshair, ConstantLine, ValueAxis, ArgumentAxis, CommonAxisSettings, ZoomAndPan, Label, VisualRange
} from 'devextreme-react/chart';
// styles
import { ThemeContext } from 'styled-components';
// utils
import { timezones } from 'utils/format';

// ----------------------------------------------------------------------

interface Props {
  infomation: any;
  datasets: any;
  lines: any;
};

// ----------------------------------------------------------------------

function MeasurementChart({ infomation, datasets, lines }: Props) {
  const ranges = useSelector((state) => state.rangesLocationSensors);
  const customizeText = (value: any) => {
    return timezones(value, 'YYYY-MM-DD HH:mm:ss');
  };

  const customizeTooltip = (point: any) => {
    const points = point.points;
    return (
      <div>
        <p style={{'fontSize': '12px', 'color': '#C3CAD9', 'marginBottom': '5px'}}>{ timezones( point.argument, 'YYYY-MM-DD HH:mm:ss') }</p>
        <div style={{'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between'}}>
          <div style={{'fontSize': '12px', 'color': '#F2F3F5', 'marginRight': '8px'}}>
            { points.map((value: any) => (
              <p key={value.seriesName} style={{'lineHeight': '20px'}}>{ value.seriesName }</p> 
            ))}
          </div>
          <div style={{'fontSize': '12px', 'color': '#FFFFFF'}}>
            { points.map((value: any) => (
              <p key={value.seriesName} style={{'lineHeight': '20px'}}>
                <span style={{'fontSize': '13px', 'fontWeight': '600'}}>{ value.valueText } </span>
                { datasets.unit[value.seriesName] }
              </p> 
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Chart dataSource={datasets.dataStore}>
      <CommonSeriesSettings type='line' argumentField='acquisitionAt' hoverMode='none' ignoreEmptyPoints={true}>
        <Point visible={false} />
      </CommonSeriesSettings>
      <CommonAxisSettings tick={{visible: false}} />
      
      { datasets.legend.map((id: any, index: number) => (
        <Series
          key={index}
          name={datasets.name[id]}
          axis={id}
          valueField={id}
          color={datasets.color[id]}
        />
      ))}
      
      { datasets.legend.map((id: any, index: number) => (
        <ValueAxis
          key={index}
          name={id}
          minValueMargin={0.01}
          maxValueMargin={0}
          minorTickInterval={0.001}
          valueMarginsEnabled={ ranges[id].manual ? false : true }
          position={ index < 2 ? 'left' : 'right' }
          placeholderSize={50}
          title={{
            text: `${datasets.name[id]} (${datasets.unit[datasets.name[id]]})`,
            alignment: 'center',
            font: {size: '11px', family: 'Pretendard, sans-serif'},
            margin: 2
          }}
        >
          <Label
            indentFromAxis={2}
            font={{size: '11px', family: 'Pretendard, sans-serif'}}
          />
          { lines === datasets.name[id] &&
            <ConstantLine
              value={datasets.alarm[id].alarmLowerLimit}
              label={{text: `${datasets.name[id]} 하한`, font: {color: '#3361FF'}}} color='#3361FF' dashStyle='dash'
            />
          }
          { lines === datasets.name[id] &&
            <ConstantLine
              value={datasets.alarm[id].alarmUpperLimit}
              label={{text: `${datasets.name[id]} 상한`, font: {color: '#E62E2E'}}} color='#E62E2E' dashStyle='dash'
            />
          }
          <VisualRange
            startValue={
              ranges[id].manual ?
                ranges[id].startValue
              : lines === datasets.name[id] && datasets.alarm[id].alarmLowerLimit ?
              Math.min( ...datasets.dataArr[id], datasets.alarm[id].alarmLowerLimit || 0, 0 )
              : Math.min( ...datasets.dataArr[id], 0 )
            }
            endValue={
              ranges[id].manual ?
                ranges[id].endValue
              : lines === datasets.name[id] && datasets.alarm[id].alarmUpperLimit ?
              Math.max( ...datasets.dataArr[id], datasets.alarm[id].alarmUpperLimit )
              : Math.max( ...datasets.dataArr[id] )
            }
          />
        </ValueAxis>
      ))}

      <Tooltip
        enabled={true}
        shared={true}
        contentRender={customizeTooltip}
        border={{visible: false}}
        color='#4D5E80F2'
        arrowLength={0}
        cornerRadius={8}
        shadow={{ offsetX: 0, offsetY: 0, blur: 5, color: '#26334D', opacity: 0.2, family: 'Pretendard, sans-serif' }}
        font={{color: '#FFFFFF'}}
      />
      <Crosshair enabled={true} width={1} />

      <ArgumentAxis argumentType='datetime'>
        <Label format={customizeText} wordWrap='none' />
      </ArgumentAxis>
      <ZoomAndPan argumentAxis='both' />
      <Legend visible={false} />
    </Chart>
  );
}

export default memo(MeasurementChart);